import React, { useCallback, useMemo, useState } from 'react'
import ErrorModalProvider from '../VideoDetailScreen/context/ErrorModalProvider'
import { FocusContext } from '@noriginmedia/norigin-spatial-navigation'
import { platformTransitionStyle, throttle } from '../../../../shared/helpers'
import ContinueWatching from '../../components/ContinueWatching'
import CollectionsSet from '../../components/CollectionsSet'
import {
  useKeyDown,
  KeyTypeEnum,
} from '../../../../shared/providers/KeyDownProvider'
import { useNavigate } from 'react-router'
import { ShowDetailContainerProps } from './types'
import { deeplinked } from '../../components/Deeplink'
import { useAppDispatch } from '../../hooks/useStore'
import { useHybridFocusable } from '../../hooks/useHybridFocusable'

import type { FocusableComponentLayout } from '@noriginmedia/norigin-spatial-navigation'

const SHOW_DETAILS_SCREEN_KEY = 'SHOW_DETAILS_SCREEN_KEY'
const COLLECTIONS_TOP_OFFSET = 80

function ShowDetailsContainer({ showDetail }: ShowDetailContainerProps) {
  const { focusKey, ref } = useHybridFocusable({
    focusKey: SHOW_DETAILS_SCREEN_KEY,
  })

  const [translateY, setTranslateY] = useState(0)
  const [descriptionModalVisible, setDescriptionModalVisible] = useState(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useKeyDown(KeyTypeEnum.Back, () => {
    if (deeplinked()) {
      navigate('/home')

      dispatch({
        type: 'DEEPLINK_URL',
        payload: {
          deeplinkedTo: false,
        },
      })
    } else {
      !descriptionModalVisible && navigate(-1)
    }
  })

  const onArrowPress = (direction: string) => {
    if (direction === 'down' && ref.current) {
      setTranslateY(ref.current.offsetHeight)
    }
    return true
  }

  const onContinueWatchingFocus = () => {
    setTranslateY(0)
  }

  const handleDescriptionModal = (visible: boolean) => {
    setDescriptionModalVisible(visible)
  }

  const handleCarouselFocus = useCallback(
    (layout: FocusableComponentLayout) => {
      setTranslateY(-(layout.top - COLLECTIONS_TOP_OFFSET))
    },
    [setTranslateY]
  )

  const handleWheel = useMemo(
    () =>
      throttle((event: React.WheelEvent<HTMLDivElement>) => {
        const deltaY = event.deltaY
        if (deltaY > 0) {
	  setTranslateY((prev) =>
           Math.max(
            -(ref.current.scrollHeight - window.innerHeight),
            prev - 480
          ))
        } else if (deltaY < 0) {
          setTranslateY((prev) => Math.min(0, prev + 480))
        }
      }, 600),
    []
  )

  const pageDetails = {
    slug: showDetail.pageDetails?.slug,
    title: showDetail.pageDetails?.title,
  }

  return (
    <FocusContext.Provider value={focusKey}>
      <ErrorModalProvider>
        <div
          ref={ref}
          className="show-detail-screen"
          style={{
            transition: platformTransitionStyle(`transform 500ms ease-in-out`),
            transform: `translateY(${translateY}px)`,
          }}
          onWheel={handleWheel}
        >
          <div className="header">
            <ContinueWatching
              onArrowPress={onArrowPress}
              onFocus={onContinueWatchingFocus}
              onDescriptionModalChange={handleDescriptionModal}
            />
          </div>
          <div className="show-detail-screen__collections">
            {showDetail.collections && showDetail.collections.length > 0 && (
              <CollectionsSet
                collections={showDetail.collections}
                handelWheelExternally
                hasHeaderComponent={true}
                hideMetadata={false}
                pageDetails={pageDetails}
                onFocus={handleCarouselFocus}
              />
            )}
          </div>
        </div>
      </ErrorModalProvider>
    </FocusContext.Provider>
  )
}

export default ShowDetailsContainer
